import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Assignment as SurveyIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  CardGiftcard as RewardIcon,
  People as PeopleIcon,
  Report as ReportIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const notificationIcons = {
  survey_submitted: <SurveyIcon color="info" />,
  survey_approved: <CheckIcon color="success" />,
  survey_rejected: <WarningIcon color="error" />,
  moderation_required: <ReportIcon color="warning" />,
  withdrawal_request: <MoneyIcon color="primary" />,
  new_user_registered: <PeopleIcon color="success" />,
  suspicious_activity: <WarningIcon color="error" />,
  reward_claimed: <RewardIcon color="primary" />
};

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    email: {
      survey_submitted: true,
      moderation_required: true,
      withdrawal_request: true,
      new_user_registered: true,
      suspicious_activity: true
    },
    push: {
      survey_submitted: true,
      moderation_required: true,
      withdrawal_request: true,
      suspicious_activity: true
    }
  });

  useEffect(() => {
    fetchNotifications();
    fetchSettings();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (e) {
      console.error('Failed to load notifications', e);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (!currentUser?.id) return;
      const res = await axios.get(`${API_BASE}/users/${currentUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.notificationSettings) setSettings(res.data.notificationSettings);
    } catch (e) {
      // ignore for now, use defaults
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
    } catch (e) {
      console.error('Failed to mark as read', e);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (e) {
      console.error('Failed to delete notification', e);
    }
  };

  const handleSettingChange = (channel, type) => {
    setSettings(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [type]: !prev[channel][type]
      }
    }));
  };

  const saveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/settings`, { settings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettingsOpen(false);
    } catch (e) {
      console.error('Failed to save settings', e);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error('Failed to mark all as read', e);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '-';
    
    const now = new Date();
    const diff = now - date;
    
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString();
    }
    return date.toLocaleDateString();
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ 
        p: 3, 
        mb: 3,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: { xs: '16px', md: '24px' },
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Уведомления администратора
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button 
              size="small" 
              onClick={handleMarkAllRead}
              sx={{
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: '#fff',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(116, 185, 255, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(116, 185, 255, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Отметить все прочитанными
            </Button>
            <IconButton onClick={() => setSettingsOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>

        <List>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  bgcolor: notification.read ? 'transparent' : 'rgba(116, 185, 255, 0.08)',
                  transition: 'background-color 0.3s',
                  borderRadius: '12px',
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: notification.read ? 'rgba(0, 0, 0, 0.02)' : 'rgba(116, 185, 255, 0.12)'
                  }
                }}
              >
                <ListItemIcon>
                  {notificationIcons[notification.type] || <SurveyIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {notification.title}
                      {!notification.read && (
                        <Chip
                          label="Новое"
                          size="small"
                          sx={{ 
                            ml: 1,
                            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                            color: '#fff',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(notification.createdAt)}
                      </Typography>
                    </>
                  }
                />
                <Box>
                  {!notification.read && (
                    <Button
                      size="small"
                      onClick={() => handleMarkAsRead(notification.id)}
                      sx={{
                        background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        boxShadow: '0 2px 8px rgba(86, 171, 47, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(86, 171, 47, 0.4)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Отметить прочитанным
                    </Button>
                  )}
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(notification.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
          {notifications.length === 0 && (
            <ListItem>
              <ListItemText
                primary="Нет уведомлений"
                secondary="Вы всё просмотрели!"
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {/* Notification Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>Настройки уведомлений</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Email уведомления
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.survey_submitted || false}
                  onChange={() => handleSettingChange('email', 'survey_submitted')}
                />
              }
              label="Новые опросы на модерацию"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.moderation_required || false}
                  onChange={() => handleSettingChange('email', 'moderation_required')}
                />
              }
              label="Требуется модерация"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.withdrawal_request || false}
                  onChange={() => handleSettingChange('email', 'withdrawal_request')}
                />
              }
              label="Запросы на вывод средств"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.new_user_registered || false}
                  onChange={() => handleSettingChange('email', 'new_user_registered')}
                />
              }
              label="Новые регистрации пользователей"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.suspicious_activity || false}
                  onChange={() => handleSettingChange('email', 'suspicious_activity')}
                />
              }
              label="Подозрительная активность"
            />
          </FormGroup>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Push уведомления
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.survey_submitted || false}
                  onChange={() => handleSettingChange('push', 'survey_submitted')}
                />
              }
              label="Новые опросы на модерацию"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.moderation_required || false}
                  onChange={() => handleSettingChange('push', 'moderation_required')}
                />
              }
              label="Требуется модерация"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.withdrawal_request || false}
                  onChange={() => handleSettingChange('push', 'withdrawal_request')}
                />
              }
              label="Запросы на вывод средств"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.suspicious_activity || false}
                  onChange={() => handleSettingChange('push', 'suspicious_activity')}
                />
              }
              label="Подозрительная активность"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}>Отмена</Button>
          <Button 
            onClick={saveSettings} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(86, 171, 47, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(86, 171, 47, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
